import { datadogRum } from '@datadog/browser-rum'
import { customerId } from '../env'
import packageJson from '../../../package.json'

/**
 * Determines service name based on CUSTOMER_ID
 * Only 2 services: diva-tme (including MM21 routes) or diva-subaru
 * @returns {string} - Service name for Datadog
 */
const getServiceName = () => {
  // Use CUSTOMER_ID to determine service name
  // MM21 routes also use diva-tme (no separate instrumentation)
  return customerId === 'SUBARU' ? 'diva-subaru' : 'diva-tme'
}

/**
 * Initialize Datadog RUM with simplified configuration
 * Only requires applicationId and clientToken, everything else uses sensible defaults
 * @param {Object} credentials - RUM credentials {applicationId, clientToken}
 */
export const initDatadogRum = credentials => {
  if (typeof window === 'undefined') {
    return
  }

  if (!credentials || !credentials.applicationId || !credentials.clientToken) {
    return
  }

  try {
    const service = getServiceName()

    datadogRum.init({
      applicationId: credentials.applicationId,
      clientToken: credentials.clientToken,
      site: 'datadoghq.com',
      service,
      env: process.env.NODE_ENV || 'dev',
      version: process.env.NEXT_PUBLIC_APP_VERSION || packageJson.version || '1.0.0',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      trackBfcacheViews: true,
      defaultPrivacyLevel: 'mask-user-input',
      trackUserInteractions: true,
      trackLongTasks: true,
      trackResources: true,
      trackErrors: true
    })

    datadogRum.setGlobalContextProperty('customerId', customerId)
    datadogRum.setGlobalContextProperty('service', service)

    datadogRum.startSessionReplayRecording()
  } catch {
    // Silent fail
  }
}

/**
 * Helper function to add custom context to RUM
 * @param {string} key - Context key
 * @param {any} value - Context value
 */
export const addRumContext = (key, value) => {
  if (typeof window !== 'undefined' && datadogRum) {
    datadogRum.setGlobalContextProperty(key, value)
  }
}

/**
 * Helper function to track custom actions
 * @param {string} name - Action name
 * @param {Object} context - Additional context
 */
export const trackRumAction = (name, context = {}) => {
  if (typeof window !== 'undefined' && datadogRum) {
    datadogRum.addAction(name, context)
  }
}

/**
 * Helper function to track errors
 * @param {Error} error - Error object
 * @param {Object} context - Additional context
 */
export const trackRumError = (error, context = {}) => {
  if (typeof window !== 'undefined' && datadogRum) {
    datadogRum.addError(error, context)
  }
}

/**
 * Helper function to track custom user information
 * @param {Object} user - User information
 */
export const setRumUser = user => {
  if (typeof window !== 'undefined' && datadogRum) {
    datadogRum.setUser(user)
  }
}

/**
 * Helper function to clear user information
 */
export const clearRumUser = () => {
  if (typeof window !== 'undefined' && datadogRum) {
    datadogRum.clearUser()
  }
}
